import { Injectable } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { combineLatest, Observable, of } from 'rxjs'
import { map } from 'rxjs/operators'
import { EbFormConfig, EbFormGroupConfig } from '../../../../form/interfaces/eb-form.interface'
import { EbFormService } from '../../../../form/services/eb-form.service'
import { EbProduct } from '../../../../product/interfaces/eb-product.interface'
import { EbCommonFormConfigService } from '../../../common/services/facades/eb-common-form-config.service'
import { EbRfpFormNumberHintComponent } from '../../components/rfp-form-number-hint/eb-rfp-form-number-hint.component'

@Injectable()
export class EbRfpFormConfigService {
  constructor(
    protected ebFormService: EbFormService,
    protected route: ActivatedRoute,
    protected commonFormConfigService: EbCommonFormConfigService
  ) {}

  get formConfig$(): Observable<EbFormConfig> {
    return this.formConfigFactory()
  }

  private formConfigFactory(): Observable<EbFormConfig> {
    return combineLatest([
      this.commonFormConfigService.getPersonalInfoGroupConfig(),
      this.commonFormConfigService.getPrivacyAndCaptchaFieldsConfig(),
      this.getSharedData()
    ]).pipe(
      map(([personalInfoGroupConfig, privacyAndCaptcha, ebProduct]) => {
        return {
          steps: [
            {
              elements: [
                {
                  name: 'materialNumber',
                  type: 'field',
                  value: ebProduct ? ebProduct.code : '',
                  fieldType: 'input'
                },
                {
                  name: 'name',
                  type: 'field',
                  value: ebProduct ? ebProduct.name : '',
                  fieldType: 'input'
                },
                { name: 'preferredQuantity', type: 'field', value: '', fieldType: 'quantity' },
                { name: 'numberInfo', type: 'component', component: EbRfpFormNumberHintComponent },
                this.getMachinInfoConfig()
              ]
            },
            {
              elements: [personalInfoGroupConfig]
            },
            {
              elements: [
                { name: 'furtherInfo', type: 'field', value: '', fieldType: 'textarea' },
                {
                  name: 'attachment',
                  type: 'field',
                  value: '',
                  fieldType: 'filedrop',
                  meta: {
                    accept: '.pdf, .jpg, .jpeg, .png',
                    maxFileSize: '25MB'
                  }
                },
                ...privacyAndCaptcha
              ]
            }
          ],
          stepsAlwaysEnabled: true
        }
      })
    )
  }

  private getMachinInfoConfig(): EbFormGroupConfig {
    const MACHINE_INFO_NAME = 'machineInfo'
    const CATEGORY_NAME = 'category'
    const CATEGORY_PATH = `${MACHINE_INFO_NAME}.${CATEGORY_NAME}`
    return {
      name: MACHINE_INFO_NAME,
      type: 'group',
      expandable: true,
      collapsed: true,
      elements: [
        { name: 'application', type: 'field', value: '', fieldType: 'input' },
        { name: 'typeOfMachine', type: 'field', value: '', fieldType: 'input' },
        { name: 'serialNumber', type: 'field', value: '', fieldType: 'input' },
        { name: 'shaftDiameter', type: 'field', value: '', fieldType: 'input' },
        { name: 'rotationSpeed', type: 'field', value: '', fieldType: 'input' },
        { name: 'machineTag', type: 'field', value: '', fieldType: 'input' },
        { name: 'pressure', type: 'field', value: '', fieldType: 'input' },
        { name: 'temperature', type: 'field', value: '', fieldType: 'input' },
        { name: 'medium', type: 'field', value: '', fieldType: 'input' },
        {
          name: 'supportsAbrasiveParticles',
          type: 'field',
          value: '',
          fieldType: 'radio',
          options: [
            { labelToken: 'yes', name: '1' },
            { labelToken: 'no', name: '0' }
          ]
        },
        {
          name: CATEGORY_NAME,
          type: 'field',
          value: '',
          fieldType: 'radio',
          options: [
            {
              name: 'mechanical_seals'
            },
            {
              name: 'packings'
            },
            {
              name: 'gaskets'
            },
            {
              name: 'other'
            }
          ]
        },
        this.getMechanicalSealInfoConfig(MACHINE_INFO_NAME, CATEGORY_PATH),
        this.createDependentGroupConfig('packingInfo', CATEGORY_PATH, 'packings', [
          { name: 'application', type: 'field', value: '', fieldType: 'input' },
          { name: 'dimensions', type: 'field', value: '', fieldType: 'input' },
          { name: 'movement', type: 'field', value: '', fieldType: 'input' },
          { name: 'geometry', type: 'field', value: '', fieldType: 'input' }
        ]),
        this.createDependentGroupConfig('gasketsInfo', CATEGORY_PATH, 'gaskets', [
          { name: 'application', type: 'field', value: '', fieldType: 'input' },
          { name: 'geometry', type: 'field', value: '', fieldType: 'input' },
          { name: 'number', type: 'field', value: '', fieldType: 'input' }
        ]),
        this.createDependentGroupConfig('otherProductInfo', CATEGORY_PATH, 'other', [
          { name: 'neededProduct', type: 'field', value: '', fieldType: 'textarea' }
        ])
      ]
    }
  }

  private createDependentGroupConfig(
    name,
    dependsOnName,
    dependsOnValue,
    elements
  ): EbFormGroupConfig {
    return {
      name,
      type: 'group',
      dependsOn: this.ebFormService.createSimpleDependsOn(dependsOnName, dependsOnValue),
      elements
    }
  }

  private getMechanicalSealInfoConfig(
    MACHINE_INFO_NAME: string,
    CATEGORY_PATH: string
  ): EbFormGroupConfig {
    const MECHANICAL_SEAL_INFO_NAME = 'mechanicalSealInfo'
    const MECHANICAL_SEAL_INFO_PATH = `${MACHINE_INFO_NAME}.${MECHANICAL_SEAL_INFO_NAME}`
    const CERTIFICATE_NAME = 'certificate'
    const CERTIFICATE_PATH = `${MECHANICAL_SEAL_INFO_PATH}.${CERTIFICATE_NAME}`
    return {
      name: MECHANICAL_SEAL_INFO_NAME,
      type: 'group',
      dependsOn: this.ebFormService.createSimpleDependsOn(CATEGORY_PATH, 'mechanicalSeals'),
      elements: [
        {
          name: 'sealingSupplySystem',
          type: 'field',
          value: '',
          fieldType: 'input'
        },
        { name: 'plan', type: 'field', value: '', fieldType: 'input' },
        {
          name: 'operatingLocation',
          type: 'field',
          value: '',
          fieldType: 'select',
          options: [
            {
              name: 'inside'
            },
            {
              name: 'outside'
            },
            {
              name: 'covered'
            }
          ]
        },
        { name: 'additionalInfo', type: 'field', value: '', fieldType: 'input' },
        {
          name: 'certificate',
          type: 'field',
          value: '',
          fieldType: 'checkbox',
          options: [
            {
              name: 'atex'
            },
            {
              name: 'EN10204'
            },
            {
              name: 'materialCertificate'
            },
            {
              name: 'testCertificate'
            }
          ],
          multiple: true
        },
        {
          name: 'conformity',
          type: 'field',
          value: '',
          fieldType: 'checkbox',
          dependsOn: this.ebFormService.createSimpleDependsOn(CERTIFICATE_PATH, 'EN10204'),
          options: [
            {
              name: 'fda'
            },
            {
              name: 'ktw'
            },
            {
              name: 'wras'
            }
          ],
          multiple: true
        },
        {
          name: 'materialCertificate',
          type: 'field',
          value: '',
          fieldType: 'input',
          dependsOn: this.ebFormService.createSimpleDependsOn(
            CERTIFICATE_PATH,
            'materialCertificate'
          )
        },
        {
          name: 'testCertificate',
          type: 'field',
          value: '',
          fieldType: 'input',
          dependsOn: this.ebFormService.createSimpleDependsOn(CERTIFICATE_PATH, 'testCertificate')
        }
      ]
    }
  }

  getSharedData(): Observable<EbProduct> {
    let product = null
    this.route.queryParams.subscribe(params => {
      product = of({
        code: params.productCode,
        name: params.productName
      } as EbProduct)
    })
    return product
  }
}
